import { IActionContext } from '../helpers/ActionContext';
import { CommonErrorCode, UserErrorCode } from '../helpers/ErrorCodes';
import { L10n } from '../L10n/L10n';
import { IDataAlertInfo } from '../models/IDataAlertInfo';
import { IUserInfo } from '../models/IUserInfo';
import { IUserSettings } from '../models/IUserSettings';
import { IErrorItem } from '../models/MultipleItemResult';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { DataAlerts } from '../resources/DataAlerts';
import { ServerUsers } from '../resources/ServerUsers';
import { IResult } from '../ServerApi/types/IResponse';
import { ActionHelpers } from './ActionHelpers';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { IErrorDetail } from './ContentActionNotification';
import { ModalService } from './ModalService';
import { ServerService } from './ServerService';

export class AddSelfToAlertsAction {

  public static $inject = [
    'ActionHelpers',
    'ConfirmActionDialog',
    'DataAlerts',
    'ServerUsers',
    '$q',
    '$translate',
    'ModalService',
    'ServerService'
  ];

  constructor(
    private ActionHelpers: ActionHelpers,
    private ConfirmActionDialog: ConfirmActionDialog,
    private DataAlerts: DataAlerts,
    private ServerUsers: ServerUsers,
    private $q: ng.IQService,
    private $translate: ng.translate.ITranslateService,
    private ModalService: ModalService,
    private ServerService: ServerService) { }

  public addSelf(alerts: Set<IDataAlertInfo>, context: IActionContext): ng.IPromise<any> {
    const self = this.ServerService.sessionUserInfo();

    return this.getUserEmailAddress(self).then((emailAddress: string) => {
      if (emailAddress) {
        return this.showConfirmDialog(alerts, context, self);
      } else {
        return this.showErrorDialogMissingEmail(alerts);
      }
    });
  }

  private showConfirmDialog(alerts: Set<IDataAlertInfo>, context: IActionContext, self: IUserInfo) {
    let dialogBodyText: string;
    let dialogTitle: string;
    if (alerts.size() === 1) {
      dialogTitle = this.$translate.instant('confirmAddSelfToAlertTitle');
      dialogBodyText = this.$translate.instant('confirmAddSelfToAlert', {
        ALERT_NAME: L10n.elideIfLong(alerts.first().name)
      });
    } else {
      dialogTitle = this.$translate.instant('confirmAddSelfToAlertsTitle');
      dialogBodyText = this.$translate.instant('confirmAddSelfToAlerts', {
        ALERT_COUNT: alerts.size()
      }, L10n.Interpolations.Count);
    }

    return this.ConfirmActionDialog.open({
      scope: context.scope,
      title: dialogTitle,
      bodyText: dialogBodyText,
      actionName: this.$translate.instant('confirmAddSelfButton'),
      action: () => this.DataAlerts.addUsers({ dataAlertIds: alerts.keys(), userIds: [ self.id ] })
        .then(result => this.notifyResult(alerts, result))
    }).result;
  }

  private getUserEmailAddress(self: IUserInfo): ng.IPromise<string> {
    return this.ServerUsers.getUserSettings(self.username, self.domainName)
        .then((userSettings: IUserSettings) => userSettings && userSettings.email);
  }

  private showErrorDialogMissingEmail(alerts: Set<IDataAlertInfo>) {
    return this.ModalService.alert({
      translatedTitle: this.$translate.instant('errorDataAlertCannotAddTitle',
          { ITEM_COUNT: alerts.size() }),
      messageId: 'errorDataAlertNoEmailAddress'
    }).result.then(() => this.$q.reject());
  }

  private notifyResult(alerts: Set<IDataAlertInfo>, result: IResult) {
    this.ActionHelpers.notifyStandardError(
      alerts,
      {
        successId: 'addSelfToAlertsSuccess',
        errorId: 'addSelfToAlertsError'
      },
      (errorItem: IErrorItem<IDataAlertInfo>) => this.errorDetailFor(errorItem)
    )(result);
  }

  private errorDetailFor(errorItem: IErrorItem<IDataAlertInfo>): IErrorDetail {
    const itemName = errorItem.item.name;
    let errorMessage: string;

    switch (errorItem.error.code) {
      case CommonErrorCode.DataAlertNotFound:
        errorMessage = this.$translate.instant('errorDataAlertNotFound', { ALERT_NAME: L10n.elideIfLong(itemName) });
        break;
      case CommonErrorCode.CallingUserInsufficientPermissions:
        errorMessage = this.$translate.instant('errorDataAlertNoPermission', { ALERT_NAME: L10n.elideIfLong(itemName) });
        break;
      case UserErrorCode.UserInvalidEmailAddress:
        errorMessage = this.$translate.instant('errorDataAlertNoEmailAddress');
        break;
      default:
        errorMessage = this.$translate.instant('errorUnknown');
    }

    return { itemName, errorMessage };
  }

}

Module.instance().service('AddSelfToAlertsAction', AddSelfToAlertsAction);
